<script setup>
import { ref, computed, watch, onMounted } from "vue";
import { useRouter } from "vue-router";
import { useDocsByTab, DOC_TABS } from "../../composables/useDocsByTab.js";
import { useDocs } from "../../composables/useDocs.js";
import { useInfiniteScroll } from "../../composables/useInfiniteScroll.js";
import { getFileTypeLabel, getFileTypeBadgeClass } from "../../composables/useFileType.js";
import { truncateMiddle } from "../../composables/useTextTruncate.js";
import { permissionsForRole } from "../../composables/usePermissions.js";
import ProjectService from "../../services/projectService.js";
import Spinner from "../../components/ui/Spinner.vue";
import DocumentCardMenu from "../../components/ui/DocumentCardMenu.vue";
import DownloadOnlyModal from "../../components/ui/DownloadOnlyModal.vue";

const router = useRouter();
const projectService = new ProjectService();
const { state: tabState, loadFirstPage, loadMore, resort: resortTab, remove: removeDoc } = useDocsByTab();
const { remove: removePanelDoc, rename: renamePanelDoc } = useDocs();

const TABS = [
  { key: DOC_TABS.ALL,            label: "All Documents" },
  { key: DOC_TABS.PERSONAL,       label: "Personal" },
  { key: DOC_TABS.SHARED_WITH_ME, label: "Shared with me" },
];
const activeTab = ref(DOC_TABS.ALL);
const current = computed(() => tabState[activeTab.value]);

const downloadOnlyDoc = ref(null); // set to open DownloadOnlyModal instead of navigating

function openDoc(doc) {
  if (doc.isAnnotatable === false) {
    downloadOnlyDoc.value = doc;
    return;
  }
  router.push({ name: "v2-document", params: { projectId: doc.projectId, docId: doc.id } });
}

// Singleton projects are real, shareable projects like any other — use the
// role the backend actually reports for the current user (getSingletonProjects
// now fetches sessionUserRole), so a doc shared to us as e.g. VIEWER doesn't
// get treated as OWNER for menu permission gating.
function ownerProjectFor(doc) {
  const role = doc.projectUserRole ?? "OWNER";
  return { id: doc.projectId, userRole: role, permissions: permissionsForRole(role), isSingleton: true };
}

// DownloadOnlyModal also needs the uploader's ownerProfile/topCollaborators
// to resolve their role badge — a singleton doc already carries those
// directly (see get-singleton-projects.graphql), unlike ownerProjectFor()'s
// minimal permission-only shape above.
function projectForDownloadModal(doc) {
  return { ...ownerProjectFor(doc), ownerProfile: doc.ownerProfile, topCollaborators: doc.topCollaborators };
}

function onDocumentDeleted(doc) {
  removeDoc(doc.id);
  removePanelDoc(doc.id); // side panel's docs list is a separate cache
}

function onDocumentRenamed(doc, { name }) {
  renamePanelDoc(doc.id, name); // side panel's docs list is a separate cache
}

async function toggleFavorite(doc) {
  const isFavorite = !doc.isFavorite;
  doc.isFavorite = isFavorite; // optimistic
  try {
    await projectService.MarkDocumentFavorite({
      documentId: doc.id,
      isFavorite,
      email: localStorage.getItem("email"),
      tenantId: localStorage.getItem("tenantId"),
    });
  } catch (error) {
    doc.isFavorite = !isFavorite; // revert on failure
  }
}

function mimeBadge(doc) {
  const mime = doc.origin?.mimeType ?? doc.originMimeType ?? "";
  return { label: getFileTypeLabel(mime), class: getFileTypeBadgeClass(mime) };
}

// Some uploads can't be rendered in the annotation viewer (isAnnotatable:
// false) — the backend still stores and lets you download them, same as the
// legacy app's "Download-only Files" section beneath the normal grid.
const annotatableDocs = computed(() => current.value.docs.filter((d) => d.isAnnotatable !== false));
const downloadOnlyDocs = computed(() => current.value.docs.filter((d) => d.isAnnotatable === false));

// A singleton doc is a real, single-document project under the hood, so its
// card shows the same role badge + collaborator avatars as a project card
// (see Projects.vue) — same helpers, since it's the same underlying concept.
const ROLE_LABELS = { OWNER: "Owner", EDITOR: "Editor", CONTRIBUTOR: "Contributor", VIEWER: "Viewer" };

function initials(person) {
  const label = person?.name || person?.email || "?";
  return label.slice(0, 2).toUpperCase();
}

function personTooltip(person) {
  if (person?.name && person?.email) return `${person.name} (${person.email})`;
  return person?.name || person?.email || "";
}

function ownerPerson(doc) {
  return doc.ownerProfile ?? null;
}

function otherCollaborators(doc) {
  return (doc.topCollaborators ?? [])
    .filter((c) => c.collaboratorRole !== "OWNER")
    .map((c) => ({ ...c.user, role: c.collaboratorRole }))
    .slice(0, 3);
}

function otherOverflowCount(doc) {
  const ownerCount = doc.ownerProfile ? 1 : 0;
  return Math.max(0, (doc.totalCollaboratorCount ?? 0) - ownerCount - otherCollaborators(doc).length);
}

const viewMode = ref("grid");
const sortBy = ref("DATE_ADDED");
const sortDir = ref("DESC"); // ASC | DESC
const showSortMenu = ref(false);

const SORT_OPTIONS = [
  { key: "DATE_ADDED",    label: "Date added" },
  { key: "LAST_MODIFIED", label: "Last modified" },
  { key: "NAME",          label: "Name" },
];

// Sort is applied server-side — changing it re-fetches from the first page.
watch([sortBy, sortDir], () => resortTab(activeTab.value, sortBy.value, sortDir.value));

watch(activeTab, (tabKey) => loadFirstPage(tabKey, sortBy.value, sortDir.value));

function selectSort(key) {
  sortBy.value = key;
  showSortMenu.value = false;
}
function toggleSortDir() {
  sortDir.value = sortDir.value === "ASC" ? "DESC" : "ASC";
}

function formatDate(ts) {
  if (!ts) return "—";
  const d = new Date(ts);
  return `${d.toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" })} at ${d.toLocaleTimeString("en-US", { hour: "numeric", minute: "2-digit" })}`;
}

const sentinelEl = ref(null);
useInfiniteScroll(sentinelEl, () => loadMore(activeTab.value));

onMounted(() => loadFirstPage(activeTab.value, sortBy.value, sortDir.value));
</script>

<template>
  <div data-testid="docs-page" class="flex flex-col h-full bg-white dark:bg-slate-900" @click="showSortMenu = false">

    <!-- Tabs -->
    <div data-testid="docs-tabs" class="flex gap-1 mb-5 pt-2 px-6 border-b border-slate-100 dark:border-slate-800 shrink-0">
      <button
        v-for="tab in TABS"
        :key="tab.key"
        :data-testid="`docs-tab-${tab.key.toLowerCase().replace(/_/g, '-')}`"
        :class="activeTab === tab.key
          ? 'text-brand border-brand font-semibold'
          : 'text-slate-500 dark:text-slate-400 border-transparent hover:text-slate-700 dark:hover:text-slate-300'"
        class="flex items-center gap-1.5 px-3 py-2.5 text-sm border-0 border-b-2 bg-transparent cursor-pointer transition-colors -mb-px"
        @click="activeTab = tab.key"
      >
        <svg v-if="tab.key === 'ALL'" width="15" height="15" viewBox="0 0 20 20" fill="none">
          <path d="M11.2507 6.25065L10.4744 4.82756C10.1824 4.29213 9.62117 3.95898 9.01126 3.95898H5.62565C4.70518 3.95898 3.95898 4.70518 3.95898 5.62565V9.16732M16.0423 8.12565V14.3757C16.0423 15.2961 15.2961 16.0423 14.3757 16.0423H5.62565C4.70518 16.0423 3.95898 15.2961 3.95898 14.3757V6.45898H14.3757C15.2961 6.45898 16.0423 7.20518 16.0423 8.12565Z" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round" />
        </svg>
        <svg v-else-if="tab.key === 'PERSONAL'" width="15" height="15" viewBox="0 0 24 24" fill="none">
          <circle cx="12" cy="8" r="3.5" stroke="currentColor" stroke-width="1.8" />
          <path d="M5 20c0-3.5 3-6 7-6s7 2.5 7 6" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" />
        </svg>
        <svg v-else width="15" height="15" viewBox="0 0 24 24" fill="none">
          <circle cx="9" cy="8" r="3" stroke="currentColor" stroke-width="1.8" />
          <path d="M2.5 19c0-3 2.9-5.5 6.5-5.5s6.5 2.5 6.5 5.5" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" />
          <path d="M15.5 5.5a3 3 0 0 1 0 5.8M18.5 19c0-2.5-1.6-4.6-4-5.3" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" />
        </svg>
        {{ tab.label }}
      </button>
    </div>

    <!-- Toolbar -->
    <div data-testid="docs-toolbar" class="flex items-center justify-between px-6 py-3 border-b bg-gray-50 dark:bg-inherit border-slate-100 dark:border-slate-800 shrink-0">
      <div class="flex items-center gap-2">
        <!-- Sort dropdown -->
        <div class="relative" @click.stop>
          <button
            data-testid="docs-sort-button"
            class="flex items-center gap-1.5 h-8 px-3 bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-lg text-sm text-slate-700 dark:text-slate-200 hover:border-slate-300 dark:hover:border-slate-600 transition-colors cursor-pointer"
            @click="showSortMenu = !showSortMenu"
          >
            <span class="text-slate-400 text-xs">Sort by</span>
            <span class="font-medium">{{ SORT_OPTIONS.find(o => o.key === sortBy)?.label }}</span>
            <svg class="text-slate-400" width="12" height="12" viewBox="0 0 24 24" fill="none">
              <path d="M6 9l6 6 6-6" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" />
            </svg>
          </button>
          <div
            v-if="showSortMenu"
            data-testid="docs-sort-menu"
            class="absolute top-full left-0 mt-1 w-44 bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-xl shadow-lg py-1 z-50"
          >
            <button
              v-for="opt in SORT_OPTIONS"
              :key="opt.key"
              :data-testid="`docs-sort-option-${opt.key.toLowerCase().replace(/_/g, '-')}`"
              :class="['flex items-center justify-between w-full px-3 py-2 text-sm text-left border-0 bg-transparent cursor-pointer transition-colors',
                sortBy === opt.key ? 'text-brand bg-orange-50 dark:bg-orange-950/30' : 'text-slate-700 dark:text-slate-200 hover:bg-slate-100 dark:hover:bg-slate-700']"
              @click="selectSort(opt.key)"
            >
              {{ opt.label }}
              <svg v-if="sortBy === opt.key" class="text-brand" width="14" height="14" viewBox="0 0 24 24" fill="none">
                <path d="M20 6L9 17l-5-5" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round" />
              </svg>
            </button>
          </div>
        </div>

        <!-- Sort direction -->
        <button
          data-testid="docs-sort-direction"
          class="flex items-center justify-center w-8 h-8 bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-lg text-slate-500 dark:text-slate-400 hover:border-slate-300 dark:hover:border-slate-600 transition-colors cursor-pointer"
          :title="sortDir === 'ASC' ? 'Ascending' : 'Descending'"
          @click="toggleSortDir"
        >
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none">
            <path d="M8 5v14M8 5L4 9M8 5l4 4" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" />
            <path d="M16 19V5M16 19l4-4M16 19l-4-4" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" />
          </svg>
        </button>
      </div>

      <!-- View mode toggle -->
      <div data-testid="docs-view-toggle" class="flex items-center bg-slate-100 dark:bg-slate-800 rounded-lg p-0.5 gap-0.5">
        <button
          :class="['flex items-center justify-center w-7 h-7 rounded-md transition-colors border-0 cursor-pointer',
            viewMode === 'grid' ? 'bg-white dark:bg-slate-700 shadow-sm text-slate-700 dark:text-slate-200' : 'bg-transparent text-slate-400 hover:text-slate-600 dark:hover:text-slate-300']"
          data-testid="docs-view-grid"
          title="Grid view"
          @click="viewMode = 'grid'"
        >
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none">
            <rect x="3" y="3" width="8" height="8" rx="1" stroke="currentColor" stroke-width="2" />
            <rect x="13" y="3" width="8" height="8" rx="1" stroke="currentColor" stroke-width="2" />
            <rect x="3" y="13" width="8" height="8" rx="1" stroke="currentColor" stroke-width="2" />
            <rect x="13" y="13" width="8" height="8" rx="1" stroke="currentColor" stroke-width="2" />
          </svg>
        </button>
        <button
          :class="['flex items-center justify-center w-7 h-7 rounded-md transition-colors border-0 cursor-pointer',
            viewMode === 'list' ? 'bg-white dark:bg-slate-700 shadow-sm text-slate-700 dark:text-slate-200' : 'bg-transparent text-slate-400 hover:text-slate-600 dark:hover:text-slate-300']"
          data-testid="docs-view-list"
          title="List view"
          @click="viewMode = 'list'"
        >
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none">
            <path d="M3 6h18M3 12h18M3 18h18" stroke="currentColor" stroke-width="2" stroke-linecap="round" />
          </svg>
        </button>
      </div>
    </div>

    <!-- Content -->
    <div class="flex-1 overflow-y-auto p-6 bg-gray-50 dark:bg-inherit">

      <!-- Loading (first page) -->
      <div v-if="current.isLoading" data-testid="docs-loading" class="flex justify-center py-20"><Spinner size="40px" /></div>

      <!-- Empty -->
      <div v-else-if="current.docs.length === 0" data-testid="docs-empty-state" class="flex flex-col items-center justify-center py-20 gap-3 text-slate-400">
        <svg width="48" height="48" viewBox="0 0 24 24" fill="none" class="text-slate-300 dark:text-slate-600">
          <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8L14 2z" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round" />
          <path d="M14 2v6h6" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round" />
        </svg>
        <p class="text-sm text-slate-400 dark:text-slate-500 m-0">No documents found</p>
      </div>

      <template v-else>
        <!-- Grid view — matches Project.vue's document cards exactly -->
        <template v-if="viewMode === 'grid'">
          <div v-if="annotatableDocs.length" data-testid="docs-grid" class="grid grid-cols-[repeat(auto-fill,minmax(275px,1fr))] gap-4">
            <div
              v-for="doc in annotatableDocs"
              :key="doc.id"
              data-testid="docs-card"
              class="relative bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-2xl overflow-hidden cursor-pointer hover:shadow-md dark:hover:border-slate-600 transition-all"
              @click="openDoc(doc)"
            >
              <div class="relative h-56 bg-slate-100 dark:bg-slate-900 flex items-center justify-center">
                <img v-if="doc.preview" :src="doc.preview" class="h-40 w-auto max-w-[75%] object-contain shadow" />
                <button
                  data-testid="docs-doc-favorite"
                  class="absolute top-4 left-4 flex items-center justify-center text-slate-400 hover:text-yellow-500 dark:hover:text-yellow-400 border-0 bg-transparent cursor-pointer transition-colors"
                  @click.stop="toggleFavorite(doc)"
                >
                  <svg width="22" height="22" viewBox="0 0 24 24" :fill="doc.isFavorite ? 'currentColor' : 'none'" :class="doc.isFavorite ? 'text-yellow-400' : ''">
                    <path d="M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z" stroke="currentColor" stroke-width="1.5" stroke-linejoin="round" />
                  </svg>
                </button>
                <span data-testid="docs-card-type" class="absolute bottom-0 left-0 px-3 py-1.5 rounded-tr-lg text-xs font-bold text-white uppercase" :class="mimeBadge(doc).class">{{ mimeBadge(doc).label }}</span>
                <span data-testid="docs-card-role" class="absolute bottom-2 right-2 flex items-center gap-1 px-2 py-1 rounded-full bg-white/90 dark:bg-slate-800/90 text-[11px] font-medium text-slate-600 dark:text-slate-300 shadow-sm">
                  <svg width="11" height="11" viewBox="0 0 24 24" fill="none">
                    <circle cx="9" cy="8" r="3" stroke="currentColor" stroke-width="1.8" />
                    <path d="M2.5 19c0-3 2.9-5.5 6.5-5.5s6.5 2.5 6.5 5.5" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" />
                  </svg>
                  {{ ROLE_LABELS[doc.projectUserRole] ?? doc.projectUserRole ?? "Owner" }}
                </span>
              </div>
              <div class="p-4">
                <div class="flex items-start justify-between gap-2">
                  <p data-testid="docs-card-name" :title="doc.name ?? 'Untitled'" class="text-base font-semibold text-slate-800 dark:text-slate-100 truncate m-0">{{ truncateMiddle(doc.name ?? "Untitled") }}</p>
                  <DocumentCardMenu data-testid="docs-doc-menu" :document="doc" :project="ownerProjectFor(doc)" @deleted="onDocumentDeleted(doc)" @rejected="onDocumentDeleted(doc)" @renamed="onDocumentRenamed(doc, $event)" />
                </div>
                <p data-testid="docs-card-modified" class="text-sm text-slate-400 dark:text-slate-500 m-0 mt-1 truncate">Last modified {{ formatDate(doc.updatedAt) }}</p>
                <div class="flex items-center gap-2 mt-2.5">
                  <div
                    v-if="ownerPerson(doc)"
                    data-testid="docs-card-owner"
                    :title="personTooltip(ownerPerson(doc))"
                    class="flex items-center justify-center w-6 h-6 rounded-full bg-[#7ba7d4] text-white text-[10px] font-semibold border-2 border-white dark:border-slate-800 shrink-0"
                  >
                    {{ initials(ownerPerson(doc)) }}
                  </div>
                  <span v-if="ownerPerson(doc) && otherCollaborators(doc).length" class="text-slate-300 dark:text-slate-600 shrink-0">&bull;</span>
                  <div class="flex items-center">
                    <div
                      v-for="(person, index) in otherCollaborators(doc)"
                      :key="person.email ?? index"
                      data-testid="docs-card-collaborator"
                      :title="personTooltip(person)"
                      :style="{ marginLeft: index !== 0 ? '-8px' : '0' }"
                      class="flex items-center justify-center w-6 h-6 rounded-full bg-[#7ba7d4] text-white text-[10px] font-semibold border-2 border-white dark:border-slate-800"
                    >
                      {{ initials(person) }}
                    </div>
                    <span v-if="otherOverflowCount(doc) > 0" data-testid="docs-card-overflow" class="ml-1 text-[11px] text-slate-400 dark:text-slate-500">+{{ otherOverflowCount(doc) }}</span>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <!-- Download-only files — can't be opened in the annotation viewer,
               stored/downloadable only (isAnnotatable: false). -->
          <div v-if="downloadOnlyDocs.length" data-testid="docs-download-only-section" :class="annotatableDocs.length ? 'mt-8' : ''">
            <h2 class="text-base font-bold text-slate-700 dark:text-slate-200 m-0 mb-1">Download-only Files</h2>
            <p class="text-sm text-slate-400 dark:text-slate-500 m-0 mb-4">These files have been included as part of this project, but are available only to download.</p>
            <div data-testid="docs-download-only-grid" class="grid grid-cols-[repeat(auto-fill,minmax(275px,1fr))] gap-4">
              <div
                v-for="doc in downloadOnlyDocs"
                :key="doc.id"
                data-testid="docs-download-only-card"
                class="relative bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-2xl overflow-hidden cursor-pointer hover:shadow-md dark:hover:border-slate-600 transition-all"
                @click="openDoc(doc)"
              >
                <div class="relative h-56 bg-slate-100 dark:bg-slate-900 flex items-center justify-center">
                  <img v-if="doc.preview" :src="doc.preview" class="h-40 w-auto max-w-[75%] object-contain shadow" />
                  <button
                    data-testid="docs-doc-favorite"
                    class="absolute top-4 left-4 flex items-center justify-center text-slate-400 hover:text-yellow-500 dark:hover:text-yellow-400 border-0 bg-transparent cursor-pointer transition-colors"
                    @click.stop="toggleFavorite(doc)"
                  >
                    <svg width="22" height="22" viewBox="0 0 24 24" :fill="doc.isFavorite ? 'currentColor' : 'none'" :class="doc.isFavorite ? 'text-yellow-400' : ''">
                      <path d="M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z" stroke="currentColor" stroke-width="1.5" stroke-linejoin="round" />
                    </svg>
                  </button>
                  <span data-testid="docs-card-type" class="absolute bottom-0 left-0 px-3 py-1.5 rounded-tr-lg text-xs font-bold text-white uppercase" :class="mimeBadge(doc).class">{{ mimeBadge(doc).label }}</span>
                  <span data-testid="docs-card-role" class="absolute bottom-2 right-2 flex items-center gap-1 px-2 py-1 rounded-full bg-white/90 dark:bg-slate-800/90 text-[11px] font-medium text-slate-600 dark:text-slate-300 shadow-sm">
                    <svg width="11" height="11" viewBox="0 0 24 24" fill="none">
                      <circle cx="9" cy="8" r="3" stroke="currentColor" stroke-width="1.8" />
                      <path d="M2.5 19c0-3 2.9-5.5 6.5-5.5s6.5 2.5 6.5 5.5" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" />
                    </svg>
                    {{ ROLE_LABELS[doc.projectUserRole] ?? doc.projectUserRole ?? "Owner" }}
                  </span>
                </div>
                <div class="p-4">
                  <div class="flex items-start justify-between gap-2">
                    <p data-testid="docs-card-name" :title="doc.name ?? 'Untitled'" class="text-base font-semibold text-slate-800 dark:text-slate-100 truncate m-0">{{ truncateMiddle(doc.name ?? "Untitled") }}</p>
                    <DocumentCardMenu data-testid="docs-doc-menu" :document="doc" :project="ownerProjectFor(doc)" @deleted="onDocumentDeleted(doc)" @rejected="onDocumentDeleted(doc)" @renamed="onDocumentRenamed(doc, $event)" />
                  </div>
                  <p data-testid="docs-card-modified" class="text-sm text-slate-400 dark:text-slate-500 m-0 mt-1 truncate">Last modified {{ formatDate(doc.updatedAt) }}</p>
                  <div class="flex items-center gap-2 mt-2.5">
                    <div
                      v-if="ownerPerson(doc)"
                      data-testid="docs-card-owner"
                      :title="personTooltip(ownerPerson(doc))"
                      class="flex items-center justify-center w-6 h-6 rounded-full bg-[#7ba7d4] text-white text-[10px] font-semibold border-2 border-white dark:border-slate-800 shrink-0"
                    >
                      {{ initials(ownerPerson(doc)) }}
                    </div>
                    <span v-if="ownerPerson(doc) && otherCollaborators(doc).length" class="text-slate-300 dark:text-slate-600 shrink-0">&bull;</span>
                    <div class="flex items-center">
                      <div
                        v-for="(person, index) in otherCollaborators(doc)"
                        :key="person.email ?? index"
                        data-testid="docs-card-collaborator"
                        :title="personTooltip(person)"
                        :style="{ marginLeft: index !== 0 ? '-8px' : '0' }"
                        class="flex items-center justify-center w-6 h-6 rounded-full bg-[#7ba7d4] text-white text-[10px] font-semibold border-2 border-white dark:border-slate-800"
                      >
                        {{ initials(person) }}
                      </div>
                      <span v-if="otherOverflowCount(doc) > 0" data-testid="docs-card-overflow" class="ml-1 text-[11px] text-slate-400 dark:text-slate-500">+{{ otherOverflowCount(doc) }}</span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </template>

        <!-- List view — matches Project.vue's table exactly -->
        <template v-else>
          <table v-if="annotatableDocs.length" data-testid="docs-table" class="w-full table-fixed border-collapse bg-white dark:bg-slate-800 rounded-lg overflow-hidden border border-slate-200 dark:border-slate-700">
            <thead>
              <tr class="bg-slate-100 dark:bg-slate-700">
                <th class="w-[55%] px-4 py-2.5 text-xs font-semibold text-slate-500 dark:text-slate-400 text-left border-b border-slate-200 dark:border-slate-700">Document name</th>
                <th class="w-[30%] px-4 py-2.5 text-xs font-semibold text-slate-500 dark:text-slate-400 text-left border-b border-slate-200 dark:border-slate-700">Last modified</th>
                <th class="w-[15%] border-b border-slate-200 dark:border-slate-700" />
              </tr>
            </thead>
            <tbody class="align-top">
              <tr
                v-for="doc in annotatableDocs"
                :key="doc.id"
                data-testid="docs-row"
                class="border-b border-slate-100 dark:border-slate-700 last:border-0 hover:bg-slate-50 dark:hover:bg-slate-700/50 transition-colors cursor-pointer"
                @click="openDoc(doc)"
              >
                <td class="px-4 py-3 text-sm font-medium text-slate-800 dark:text-slate-100">
                  <div class="flex items-start gap-2 min-w-0">
                    <button data-testid="docs-doc-favorite" class="shrink-0 flex h-5 items-center justify-center text-slate-300 dark:text-slate-600 hover:text-yellow-500 dark:hover:text-yellow-400 border-0 bg-transparent cursor-pointer transition-colors" @click.stop="toggleFavorite(doc)">
                      <svg width="14" height="14" viewBox="0 0 24 24" :fill="doc.isFavorite ? 'currentColor' : 'none'" :class="doc.isFavorite ? 'text-yellow-400' : ''">
                        <path d="M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z" stroke="currentColor" stroke-width="1.5" stroke-linejoin="round" />
                      </svg>
                    </button>
                    <span data-testid="docs-row-name" class="min-w-0 [overflow-wrap:anywhere]">{{ doc.name ?? "Untitled" }}</span>
                  </div>
                </td>
                <td data-testid="docs-row-modified" class="px-4 py-3 text-sm text-slate-500 dark:text-slate-400">{{ formatDate(doc.updatedAt) }}</td>
                <td class="px-4 py-3 text-right" @click.stop>
                  <DocumentCardMenu data-testid="docs-doc-menu" :document="doc" :project="ownerProjectFor(doc)" @deleted="onDocumentDeleted(doc)" @rejected="onDocumentDeleted(doc)" @renamed="onDocumentRenamed(doc, $event)" />
                </td>
              </tr>
            </tbody>
          </table>

          <!-- Download-only files -->
          <div v-if="downloadOnlyDocs.length" data-testid="docs-download-only-section" :class="annotatableDocs.length ? 'mt-8' : ''">
            <h2 class="text-base font-bold text-slate-700 dark:text-slate-200 m-0 mb-1">Download-only Files</h2>
            <p class="text-sm text-slate-400 dark:text-slate-500 m-0 mb-4">These files have been included as part of this project, but are available only to download.</p>
            <table data-testid="docs-download-only-table" class="w-full table-fixed border-collapse bg-white dark:bg-slate-800 rounded-lg overflow-hidden border border-slate-200 dark:border-slate-700">
              <thead>
                <tr class="bg-slate-100 dark:bg-slate-700">
                  <th class="w-[55%] px-4 py-2.5 text-xs font-semibold text-slate-500 dark:text-slate-400 text-left border-b border-slate-200 dark:border-slate-700">Document name</th>
                  <th class="w-[30%] px-4 py-2.5 text-xs font-semibold text-slate-500 dark:text-slate-400 text-left border-b border-slate-200 dark:border-slate-700">Last modified</th>
                  <th class="w-[15%] border-b border-slate-200 dark:border-slate-700" />
                </tr>
              </thead>
              <tbody class="align-top">
                <tr
                  v-for="doc in downloadOnlyDocs"
                  :key="doc.id"
                  data-testid="docs-download-only-row"
                  class="border-b border-slate-100 dark:border-slate-700 last:border-0 hover:bg-slate-50 dark:hover:bg-slate-700/50 transition-colors cursor-pointer"
                  @click="openDoc(doc)"
                >
                  <td class="px-4 py-3 text-sm font-medium text-slate-800 dark:text-slate-100">
                    <div class="flex items-start gap-2 min-w-0">
                      <button data-testid="docs-doc-favorite" class="shrink-0 flex h-5 items-center justify-center text-slate-300 dark:text-slate-600 hover:text-yellow-500 dark:hover:text-yellow-400 border-0 bg-transparent cursor-pointer transition-colors" @click.stop="toggleFavorite(doc)">
                        <svg width="14" height="14" viewBox="0 0 24 24" :fill="doc.isFavorite ? 'currentColor' : 'none'" :class="doc.isFavorite ? 'text-yellow-400' : ''">
                          <path d="M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z" stroke="currentColor" stroke-width="1.5" stroke-linejoin="round" />
                        </svg>
                      </button>
                      <span data-testid="docs-row-name" class="min-w-0 [overflow-wrap:anywhere]">{{ doc.name ?? "Untitled" }}</span>
                    </div>
                  </td>
                  <td data-testid="docs-row-modified" class="px-4 py-3 text-sm text-slate-500 dark:text-slate-400">{{ formatDate(doc.updatedAt) }}</td>
                  <td class="px-4 py-3 text-right" @click.stop>
                    <DocumentCardMenu data-testid="docs-doc-menu" :document="doc" :project="ownerProjectFor(doc)" @deleted="onDocumentDeleted(doc)" @rejected="onDocumentDeleted(doc)" @renamed="onDocumentRenamed(doc, $event)" />
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        </template>

        <!-- Infinite scroll sentinel -->
        <div ref="sentinelEl" data-testid="docs-scroll-sentinel" class="h-px" />
        <div v-if="current.isLoadingMore" data-testid="docs-loading-more" class="flex justify-center py-6"><Spinner size="24px" /></div>
        <p v-else-if="!current.hasMore" data-testid="docs-end-message" class="text-center text-xs text-slate-400 dark:text-slate-500 py-6 m-0">You've reached the end</p>
      </template>
    </div>

    <DownloadOnlyModal
      v-if="downloadOnlyDoc"
      data-testid="docs-download-only-modal"
      :document="downloadOnlyDoc"
      :project="projectForDownloadModal(downloadOnlyDoc)"
      @close="downloadOnlyDoc = null"
    />
  </div>
</template>
